'use client';

import type { FC } from 'react';
import { useSearchParams } from 'next/navigation';

import { usePushQueryChange } from '@packages/shared/src/hooks/usePushQueryChange/usePushQueryChange';
import { useUrl } from '@packages/shared/src/hooks/useUrl/useUrl';
import { mergeQueryParameters } from '@packages/shared/src/utils/mergeQueryParameters/mergeQueryParameters';
import { rememberPageData } from '@packages/shared/src/hooks/useScrollRestoration/useScrollRestoration';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { SearchProductCardItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardItemFragmentFragmentDoc';
import type { SearchProductCardResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardResultFragmentFragmentDoc';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';

import { ProductCardV2Name } from '@packages/modules/src/ProductCardV2/ProductCardV2.Name';
import { ProductCardV2Brand } from '@packages/modules/src/ProductCardV2/ProductCardV2.Brand';
import { ProductCardV2Image } from '@packages/modules/src/ProductCardV2/ProductCardV2.Image';
import { ProductCardV2FlagsContainer } from '@packages/modules/src/ProductCardV2/ProductCardV2.FlagsContainer';
import { ProductCardV2CustomFlag } from '@packages/modules/src/ProductCardV2/ProductCardV2.CustomFlag';
import { ProductCardV2WishlistButton } from '@packages/modules/src/ProductCardV2/ProductCardV2.WishlistButton';
import { ProductCardV2ColorSwitcher } from '@packages/modules/src/ProductCardV2/ProductCardV2.ColorSwitcher.client';
import { ProductCardV2ExtendedDescription } from '@packages/modules/src/ProductCardV2/ProductCardV2.ExtendedDescription';
import { ProductCardV2Rating } from '@packages/modules/src/ProductCardV2/ProductCardV2.Rating';
import { ProductCardV2EnergyEfficiencyFlags } from '@packages/modules/src/ProductCardV2/ProductCardV2.EnergyEfficiencyFlags.client';
import { ProductCardV2EfficiencyFlagsContainer } from '@packages/modules/src/ProductCardV2/ProductCardV2.EfficiencyFlagsContainer';
import { ProductCardV2TireEfficiencyFlags } from '@packages/modules/src/ProductCardV2/ProductCardV2.TireEfficiencyFlags.client';
import { ProductCardV2PowerSupply } from '@packages/modules/src/ProductCardV2/ProductCardV2.PowerSupply';
import { ProductCardV2SustainabilityIconsContainer } from '@packages/modules/src/ProductCardV2/ProductCardV2.SustainabilityIconsContainer';
import type { SearchProductCardFullVariationGroupFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardFullVariationGroupFragmentFragmentDoc';
import { SustainabilityIcon } from '@packages/modules/src/SustainabilityIcon/SustainabilityIcon.client';
import { Flag } from '@packages/shared/src/ui/Flag/Flag';

import { useHrefWithPageNumber } from '../../hooks/useHrefWithPageNumber';
import { useSearchProductCardTracking } from './tracking/useSearchProductCardTracking';
import { buildGlycerinTrackingData } from './tracking/buildGlycerinTrackingData';
import { encodePage } from '../../queryEncoding';
import { sustainabilityIcon } from '../../activeFeatureFlags';
import { useDeferredVariationGroup } from './useDeferredVariationGroup';
import { useMappedEfficiencyFlags2 } from '../../hooks/useMappedEfficiencyFlags';
import { getTestId } from '../../utils/featureFlags/getTestId';
import { hasFeatureFlag } from '../../utils/featureFlags/hasFeatureFlag';
import { mapSearchPowerSupplyData } from '../../utils/mapSearchPowerSupplyData';
import { getDeviceIndependentDisplayMode } from '../../utils/getDeviceIndependentDisplayMode';
import { ProductListPrice } from '../ProductList/Cards/ProductList.Price';
import { ProductListAvailabilityOverlay } from '../ProductList/Cards/ProductList.AvailabilityOverlay';

/* GraphQL */ `
  fragment SearchProductCardResultFragment on SearchProductResult {
    id
    ...SearchProductCardImpressionTrackingResultFragment
    ...SearchProductCardClickTrackingResultFragment
    ...SearchProductCardGlycerinTrackingResultFragment
    ...UseDisplayModeFragment
    abTesting {
      newTestAssignments {
        testId
      }
    }
  }
`;

// Moved to ProductList.AvailabilityOverlay.tsx

/* GraphQL */ `
  fragment SearchProductCardFullVariationGroupFragment on CompleteVariationGroup {
    groupName
    groupDescription
    image
    imageForHighlight
    sku # only for tracking?
    articleNumber # only for tracking
    href
    price {
      ...ProductListPriceFragment
    }
    availability {
      ...ProductListAvailabilityOverlayFragment
    }
    customFlag {
      # description
      image
    }
    standardFlags {
      type
      text
    }
    searchPowerSupply {
      ...SearchPowerSupplyFragment
    }
  }
`;

/* GraphQL */ `
  fragment SearchProductCardItemFragment on StylePreviewItemBase {
    styleId # needed?
    styleName
    categoryName # only for tracking
    isHighlight
    brand {
      name
    }
    efficiencyFlags {
      ...MapEfficiencyFlagsFragment
    }
    ratings {
      averageValue
      count
    }
    sellingPoints
    primaryVariationGroup {
      sku
      ...SearchProductCardFullVariationGroupFragment
      ...SearchProductCardClickTrackingVariationGroupFragment
      ...SearchProductCardGlycerinTrackingVariationGroupFragment
    }
    variationGroups {
      groupName
      icon {
        image
      }
    }
    sustainabilities {
      iconUrl
      value
    }
    ...SearchProductCardImpressionTrackingItemFragment
    ...SearchProductCardClickTrackingItemFragment
    ...SearchProductCardGlycerinTrackingItemFragment
  }
`;

const standardFlagTypeMap = {
  DEFAULT: 'neutral',
  SECONDARY: 'secondary',
  SUSTAINABILITY: 'sustainability',
} as const;

export type SearchProductCardContentProps = {
  maskedItem: FragmentType<typeof SearchProductCardItemFragmentFragmentDoc>;
  maskedResult: FragmentType<typeof SearchProductCardResultFragmentFragmentDoc>;
  pageNumber: number;
  priority?: boolean;
  onClick?: () => void;
  hasUserInteraction: boolean;
  // NOTE this is only here for storybook compat for now, because the wishlist button can't render in storybook due to technical issues with MUI icons
  hideWishlistButton?: boolean;
};

/**
 * A search-page-specific wrapper around `ProductCard`
 */
export const SearchProductCardContent: FC<SearchProductCardContentProps> = (props) => {
  const searchParams = useSearchParams();

  const {
    maskedItem,
    maskedResult,
    pageNumber,
    priority,
    onClick,
    hasUserInteraction,
    hideWishlistButton,
  } = props;

  const result = unmask<typeof SearchProductCardResultFragmentFragmentDoc>(maskedResult);
  const item = unmask<typeof SearchProductCardItemFragmentFragmentDoc>(maskedItem);

  const {
    maskedDeferredVariationGroup,
    activeVariationIndex,
    setActiveVariationIndex,
    isActiveVariationLoading,
  } = useDeferredVariationGroup({
    resultId: result.id,
    styleId: item.styleId,
  });

  // if full variation data is not yet available, fall back to the first variation until data has loaded
  const maskedActiveVariationGroup = maskedDeferredVariationGroup ?? item.primaryVariationGroup;
  const activeVariationGroup = unmask<
    typeof SearchProductCardFullVariationGroupFragmentFragmentDoc
  >(maskedActiveVariationGroup);

  const { isMobile } = useDeviceType();

  const { dispatchProductClickTracking, dispatchColorChangeTracking } =
    useSearchProductCardTracking(result, item, maskedActiveVariationGroup, pageNumber);

  const { wishListTrackingProps } = buildGlycerinTrackingData(
    result,
    item,
    maskedActiveVariationGroup,
  );

  const { displayMode } = getDeviceIndependentDisplayMode(result, searchParams);
  const { setRelevantProduct, setOutcome } = useAbTesting();
  const { getCookies } = useCookies();

  // NOTE: for some reason this does not update with `history.replaceState` correctly, but the page is overwritten below anyways
  const url = useUrl(true);
  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  const handleClick = () => {
    dispatchProductClickTracking();

    const queryChange = encodePage(pageNumber);
    pushQueryChange(queryChange, { shallow: true });

    const modifiedUrl = mergeQueryParameters(url, queryChange);
    rememberPageData(item.styleId, modifiedUrl);

    // later steps in the customer flow need to be able to correlate the product with the test (e.g. in detail view or checkout)
    for (const testAssignment of result.abTesting?.newTestAssignments ?? []) {
      setRelevantProduct(testAssignment.testId, item.styleId);
    }

    onClick?.();
  };

  const handleColorChange = (index: number) => {
    setActiveVariationIndex(index);
    dispatchColorChangeTracking();
  };

  const href = useHrefWithPageNumber(activeVariationGroup.href, pageNumber);
  const name = activeVariationGroup.groupDescription ?? item.styleName;

  const customFlag = activeVariationGroup.customFlag
    ? { type: 'custom' as const, src: activeVariationGroup.customFlag.image }
    : undefined;
  const standardFlags = activeVariationGroup.standardFlags
    .map(({ type, text }) => ({
      type: standardFlagTypeMap[type],
      text,
    }))
    .slice(0, customFlag ? 2 : 3);

  const colors = item.variationGroups.map((variationGroup, index) => ({
    name: variationGroup.groupName ?? '',
    image: variationGroup.icon?.image,
    active: index === activeVariationIndex,
  }));

  const { energyEfficiencyFlags, tireEfficiencyFlags } = useMappedEfficiencyFlags2(
    item.efficiencyFlags,
  );

  const productHasSustainabilityData = (item.sustainabilities?.length ?? 0) > 0;

  // remove if A/B test is finished
  if (productHasSustainabilityData) {
    setRelevantProduct(getTestId(sustainabilityIcon), item.styleId);
  }

  const shouldShowSustainabilityIcon =
    hasFeatureFlag(sustainabilityIcon, getCookies()) && productHasSustainabilityData;

  const powerSupplyProps = activeVariationGroup.searchPowerSupply && {
    ...mapSearchPowerSupplyData(activeVariationGroup.searchPowerSupply),
    asCard: false,
    showPowerSupplyMessage: false,
  };

  return (
    <>
      <ProductCardV2Name id={item.styleId} href={href} onClick={handleClick}>
        {name}
      </ProductCardV2Name>
      <ProductCardV2Brand id={item.styleId}>{item.brand.name}</ProductCardV2Brand>

      {displayMode === 'moreInformation' && (
        <ProductCardV2ExtendedDescription bulletPoints={item.sellingPoints} />
      )}

      {/* prevent rendering the overlay for every card, because it doesn't matter until the user hovers the card */}
      {hasUserInteraction && activeVariationGroup.availability && (
        <ProductListAvailabilityOverlay
          href={href}
          groupName={activeVariationGroup.groupName ?? undefined}
          maskedAvailability={activeVariationGroup.availability}
        />
      )}

      <ProductListPrice styleId={item.styleId} maskedPrice={activeVariationGroup.price} />

      {item.ratings && (
        <ProductCardV2Rating
          id={item.styleId}
          avgRating={item.ratings.averageValue}
          reviewCount={item.ratings.count}
        />
      )}

      {((energyEfficiencyFlags?.length ?? 0) > 0 || (tireEfficiencyFlags?.length ?? 0) > 0) && (
        <ProductCardV2EfficiencyFlagsContainer>
          {energyEfficiencyFlags && energyEfficiencyFlags.length > 0 && (
            <ProductCardV2EnergyEfficiencyFlags flags={energyEfficiencyFlags} />
          )}
          {tireEfficiencyFlags && tireEfficiencyFlags.length > 0 && (
            <ProductCardV2TireEfficiencyFlags flags={tireEfficiencyFlags} />
          )}
        </ProductCardV2EfficiencyFlagsContainer>
      )}

      {shouldShowSustainabilityIcon && (
        <ProductCardV2SustainabilityIconsContainer>
          {item.sustainabilities?.map((sustainability) => (
            <li>
              <SustainabilityIcon
                iconUrl={sustainability.iconUrl}
                value={sustainability.value}
                onClick={() => {
                  // remove if A/B test is finished
                  setOutcome(getTestId(sustainabilityIcon), {
                    COF: 1,
                  });
                }}
                modalContent={<ServiceLayer serviceLayerId={sustainability.value} />}
              />
            </li>
          ))}
        </ProductCardV2SustainabilityIconsContainer>
      )}

      {powerSupplyProps && <ProductCardV2PowerSupply {...powerSupplyProps} />}

      {/* TODO SEARCH-2950 move everything above here to `ProductList.SharedCardContent.tsx` */}

      <ProductCardV2Image
        src={
          // show highlight image when product is highlight, unless in "Mobile Listenansicht" or "Tablet Listenansicht" mode
          item.isHighlight &&
          activeVariationGroup.imageForHighlight &&
          !(isMobile && displayMode === 'moreInformation')
            ? activeVariationGroup.imageForHighlight
            : activeVariationGroup.image
        }
        alt=""
        priority={priority}
      />

      <ProductCardV2FlagsContainer>
        {activeVariationGroup.customFlag && (
          <ProductCardV2CustomFlag src={activeVariationGroup.customFlag.image} alt="" />
        )}
        {standardFlags.map(({ type, text }) => (
          <Flag key={text} variant={type} text={text} />
        ))}
      </ProductCardV2FlagsContainer>

      {wishListTrackingProps && !hideWishlistButton && (
        <ProductCardV2WishlistButton
          productName={`${item.brand.name} ${name}`}
          sku={activeVariationGroup.sku}
          wishListTrackingProps={wishListTrackingProps}
        />
      )}

      {colors && (
        <ProductCardV2ColorSwitcher
          colors={colors}
          activeIndex={activeVariationIndex}
          onColorChange={handleColorChange}
          isLoading={isActiveVariationLoading}
        />
      )}
    </>
  );
};
